/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import User, {UserFields} from '../../../models/userModel';
import {ActivateHandle} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import {PersonalErrors} from "../../settings/common/userErrorsInterface";
import Subscription from "../../../models/subscriptionModel";
import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import ExistingUserSubscription from "./existingUserSubscription";
import {createErrorMessage} from "../../../util/utils";

type Errors = Pick<PersonalErrors, 'address' | 'zipCode' | 'city'>;

const emptyErrors: Errors = {address: null, city: null, zipCode: null};

function ExistingUserSubscriptionController(props: {}, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const [user, setUser] = useState(context.userContext.user ?? new User());
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Errors>(emptyErrors);
    const [hasFullAddress, setHasFullAddress] = useState(false);

    function onActivate() {
        const {user} = context.userContext;
        if (!user) return;
        setUser(user.clone());
        setHasFullAddress(user.hasFullAddress());
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setErrors(emptyErrors);
        setHasFullAddress(false);
    }

    function onCancel() {
        window.history.back();
    }

    function onChange(key: keyof UserFields) {
        return function (value: any) {
            setUser(user.clone().setField(key, value));
        }
    }

    function onResetError(key: keyof Errors) {
        return function () {
            setErrors({...errors, [key]: null});
        }
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        user.createSubscription((subscription: Subscription | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                user.subscription = subscription;
                context.onChangeContext({user});
                onClose();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het starten van de proefperiode is niet gelukt.'),
                    'Proefperiode starten mislukt', [{label: 'Oké', onClick: window.history.back}]);
        });
    }

    function validateValues(): boolean {
        const _errors = {...errors};
        let valid: boolean = true;
        if (!user.address) {
            _errors.address = 'Vul het adres in';
            valid = false;
        }
        if (!user.zipCode) {
            _errors.zipCode = 'Vul de postcode in';
            valid = false;
        }
        if (!user.city) {
            _errors.city = 'Vul de plaats in';
            valid = false;
        }
        if (valid) setErrors(_errors);
        return valid;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <ExistingUserSubscription onCancel={onCancel} onSubmit={onSubmit} onChange={onChange}
                                  onResetError={onResetError} active={active} user={user} loading={loading}
                                  errors={errors} hasFullAddress={hasFullAddress}/>
    )
}

export default forwardRef<ActivateHandle, {}>(ExistingUserSubscriptionController);